<template>
  <section id="contact">
    <div class="contact-wrapper">
      <div class="contact-header">
        <h2>Get In Touch</h2>
        <p class="subtitle">Have a project in mind or want to collaborate? I'd love to hear from you!</p>
      </div>

    <div class="contact-content">
      <!-- Contact Info Cards -->
      <div class="contact-info">
        <div class="info-card">
          <i class="fas fa-map-marker-alt"></i>
          <h3>Location</h3>
          <p>Cyprus</p>
        </div>
        <div class="info-card">
          <i class="fas fa-clock"></i>
          <h3>Time Zone</h3>
          <p>{{ currentTimezone }}</p>
        </div>
        <div class="info-card">
          <i class="fas fa-globe"></i>
          <h3>Remote Work</h3>
          <p>Available Worldwide</p>
        </div>
      </div>

      <!-- Contact Form -->
      <div class="form-container">
        <form @submit.prevent="submitForm" :class="{ 'loading': isSubmitting }">
          <div class="form-group">
            <label for="name">
              <i class="fas fa-user"></i>
              Your Name
            </label>
            <input 
              type="text" 
              id="name" 
              v-model="name" 
              placeholder="John Doe"
              required
              :disabled="isSubmitting"
            >
          </div>

          <div class="form-group">
            <label for="email">
              <i class="fas fa-envelope"></i>
              Email Address
            </label>
            <input 
              type="email" 
              id="email" 
              v-model="email" 
              placeholder="john@example.com"
              required
              :disabled="isSubmitting"
            >
          </div>

          <div class="form-group">
            <label for="message">
              <i class="fas fa-comment-alt"></i>
              Your Message
            </label>
            <textarea 
              id="message" 
              v-model="message" 
              @input="limitText" 
              placeholder="Tell me about your project or inquiry..."
              required
              :disabled="isSubmitting"
            ></textarea>
            <div class="char-count" :class="{ 'warning': message.length > 900 }">
              <span>{{ message.length }} / 1000</span>
            </div>
          </div>

          <button type="submit" :disabled="isSubmitting" class="submit-btn">
            <span v-if="!isSubmitting">
              <i class="fas fa-paper-plane"></i>
              Send Message
            </span>
            <span v-else>
              <i class="fas fa-spinner fa-spin"></i>
              Sending...
            </span>
          </button>
        </form>

        <!-- Success/Error Messages -->
        <transition name="fade">
          <div v-if="showMessage" :class="['message', messageType]">
            <i :class="messageIcon"></i>
            <p>{{ messageText }}</p>
          </div>
        </transition>
      </div>
    </div>
    </div>
  </section>
</template>

<script>
import {config} from '@/config.js';
export default {
  name: 'ContactForm',
  data() {
    return {
      name: '',
      email: '',
      message: '',
      isSubmitting: false,
      showMessage: false,
      messageType: '',
      messageText: '',
      messageIcon: ''
    };
  },
  computed: {
    currentTimezone() {
      // Cyprus uses Eastern European Time (EET) in winter and Eastern European Summer Time (EEST) in summer
      const now = new Date();
      const year = now.getFullYear();
      
      // Last Sunday of March at 3:00 AM (start of DST)
      const dstStart = new Date(year, 2, 31); // March 31
      dstStart.setDate(dstStart.getDate() - dstStart.getDay()); // Last Sunday
      dstStart.setHours(3, 0, 0, 0);
      
      // Last Sunday of October at 4:00 AM (end of DST)
      const dstEnd = new Date(year, 9, 31); // October 31
      dstEnd.setDate(dstEnd.getDate() - dstEnd.getDay()); // Last Sunday
      dstEnd.setHours(4, 0, 0, 0);
      
      // Check if current date is in DST period
      const isDST = now >= dstStart && now < dstEnd;
      
      return isDST ? 'EEST (UTC+3)' : 'EET (UTC+2)';
    }
  },
  methods: {
    limitText() {
      if (this.message.length > 1000) {
        this.message = this.message.slice(0, 1000);
      }
    },
    showNotification(type, text) {
      this.messageType = type;
      this.messageText = text;
      this.messageIcon = type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle';
      this.showMessage = true;
      
      setTimeout(() => {
        this.showMessage = false;
      }, 5000);
    },
    resetForm() {
      this.name = '';
      this.email = '';
      this.message = '';
    },
    async submitForm() {
      this.isSubmitting = true;
      
      const formData = {
        name: this.name,
        email: this.email,
        text: this.message
      };

      try {
        const response = await fetch(`${config.API_BASE_URL}/contact_form`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          this.showNotification('success', 'Message sent successfully! I\'ll get back to you soon.');
          this.resetForm();
        } else {
          this.showNotification('error', 'Failed to send message. Please try again later.');
        }
      } catch (error) {
        console.error('Error:', error);
        this.showNotification('error', 'An error occurred. Please check your connection and try again.');
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
/* Global box-sizing for this component */
* {
  box-sizing: border-box;
}

/* Base Section Styling */
section {
  max-width: 800px;
  margin: 0 auto 2rem;
}

/* Contact Wrapper - matches other page styling */
.contact-wrapper {
  background: #fff;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  padding: 2rem;
}

/* Header Section */
.contact-header {
  text-align: center;
  margin-bottom: 2rem;
}

.contact-header h2 {
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.subtitle {
  color: #666;
  font-size: 1.1rem;
  line-height: 1.6;
}

/* Contact Content Layout */
.contact-content {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

/* Contact Info Cards */
.contact-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.contact-info .info-card {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #e9ecef;
}

.contact-info .info-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 30px rgba(0, 0, 0, 0.12);
}

.contact-info .info-card i {
  font-size: 2rem;
  color: #007acc;
  margin-bottom: 1rem;
  display: block;
}

.contact-info .info-card h3 {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.contact-info .info-card p {
  color: #666;
  font-size: 0.95rem;
  margin: 0;
}

/* Form Container */
.form-container {
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
}

/* Form Styling */
form {
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: opacity 0.3s ease;
  box-sizing: border-box;
}

form.loading {
  opacity: 0.7;
}

/* Form Groups */
.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.form-group label i {
  color: #007acc;
  font-size: 0.9rem;
}

/* Input Styling */
.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e1e8ed;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  font-family: inherit;
  background-color: #f8f9fa;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #007acc;
  background-color: #fff;
  box-shadow: 0 0 0 3px rgba(0, 122, 204, 0.1);
}

.form-group input:disabled,
.form-group textarea:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
  max-height: 300px;
}

/* Character Count */
.char-count {
  text-align: right;
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: #6c757d;
  transition: color 0.3s ease;
}

.char-count.warning {
  color: #ff6b6b;
  font-weight: 500;
}

/* Submit Button */
.submit-btn {
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #007acc, #005a9e);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 15px rgba(0, 122, 204, 0.3);
  box-sizing: border-box;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 122, 204, 0.4);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.submit-btn i {
  font-size: 0.9rem;
}

/* Message Notifications */
.message {
  margin-top: 1.5rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  animation: slideIn 0.3s ease;
}

.message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.message i {
  font-size: 1.2rem;
}

.message p {
  margin: 0;
  flex: 1;
}

/* Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  section {
    padding: 1rem 0.5rem;
  }

  .contact-wrapper {
    padding: 1.5rem;
  }

  .contact-header h2 {
    font-size: 2rem;
  }

  .subtitle {
    font-size: 1rem;
  }

  .contact-info {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .contact-info .info-card {
    padding: 1.25rem;
  }

  form {
    padding: 1.25rem;
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .submit-btn {
    padding: 0.875rem 1.5rem;
  }
}

/* Dark Mode Support (if implemented) */
@media (prefers-color-scheme: dark) {
  /* Add dark mode styles here if needed */
}

/* Placeholder Styling */
::placeholder {
  color: #adb5bd;
  opacity: 1;
}

/* Focus Visible for Accessibility */
*:focus-visible {
  outline: 2px solid #007acc;
  outline-offset: 2px;
}
</style>